import * as JSON5 from "json5"

import type { SimulationOutput } from "@/types/RunRow"

type TranscriptMessageWithLegacyTimestamps = Record<string, unknown> & {
  start_time?: unknown
  end_time?: unknown
}

function isTranscriptMessageRecord(
  value: unknown,
): value is TranscriptMessageWithLegacyTimestamps {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

/**
 * Maps timestamp attributes in a transcript message
 * Converts start_time to beginning and end_time to end
 *
 * @param message A transcript message object
 * @returns The updated message with renamed timestamp attributes
 */
function mapTimestampAttributes(message: unknown): unknown {
  if (!isTranscriptMessageRecord(message)) {
    return message
  }

  // Create a copy of the message
  const updatedMessage = { ...message }

  // Map start_time to beginning if it exists
  if ("start_time" in updatedMessage) {
    updatedMessage.beginning = updatedMessage.start_time
    delete updatedMessage.start_time
  }

  // Map end_time to end if it exists
  if ("end_time" in updatedMessage) {
    updatedMessage.end = updatedMessage.end_time
    delete updatedMessage.end_time
  }

  return updatedMessage
}

/**
 * Parses simulation output string and sets transcript and transcriptType.
 * This utility function centralizes the common pattern used across multiple files.
 * Also maps timestamp attributes in messages from start_time/end_time to beginning/end.
 *
 * @param output The simulation output object to update
 * @returns The updated simulation output object
 */
export function parseSimulationOutput<
  T extends Pick<
    SimulationOutput,
    "transcript" | "transcript_type" | "simulation_output_str"
  >,
>(output: T): T {
  // Skip if transcript already exists and has content
  if (Array.isArray(output.transcript) && output.transcript.length > 0) {
    return output
  }
  if (!output.simulation_output_str) {
    return output
  }

  try {
    const transcript: unknown = JSON5.parse(output.simulation_output_str)

    // Check if it has the messages property (OpenAI format)
    if (
      isTranscriptMessageRecord(transcript) &&
      Array.isArray(transcript.messages)
    ) {
      // Map timestamp attributes in each message
      output.transcript = transcript.messages.map(
        (message) =>
          mapTimestampAttributes(
            message,
          ) as SimulationOutput["transcript"][number],
      )
    } else if (Array.isArray(transcript)) {
      // If transcript is already an array, map timestamp attributes
      output.transcript = transcript.map(
        (message) =>
          mapTimestampAttributes(
            message,
          ) as SimulationOutput["transcript"][number],
      )
    } else {
      // For any other format, ensure transcript is an empty array
      output.transcript = []
    }

    output.transcript_type = "openai-v1"
  } catch {
    // If parsing fails, set transcript_type to string
    output.transcript_type = "string"
  }

  return output
}
